import { useState, useMemo } from 'react';
import { Sparkles, Copy, Check, Trash2, RotateCcw, Loader2, Wand2, ChevronLeft, ChevronRight, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useToolUsage } from '@/hooks/useToolUsage';
import { callTAMAI } from '@/lib/tamAI';
import { cn } from '@/lib/utils';

function getHighlightedOutput(original: string, humanized: string): React.ReactNode[] {
    if (!original || !humanized) return [humanized];
    
    const originalWords = original.split(/(\s+)/);
    const humanizedWords = humanized.split(/(\s+)/);
    
    const originalWordSet = new Set(
        originalWords
            .filter(w => w.trim())
            .map(w => w.toLowerCase().replace(/[.,!?;:'"()]/g, ''))
    );
    
    return humanizedWords.map((word, index) => {
        const cleanWord = word.toLowerCase().replace(/[.,!?;:'"()]/g, '');
        
        if (!word.trim()) {
            return word;
        }
        
        const isChanged = cleanWord.length > 2 && !originalWordSet.has(cleanWord);
        
        if (isChanged) {
            return (
                <span key={index} className="text-green-500 dark:text-green-400 underline decoration-green-500/30">
                    {word}
                </span>
            );
        }
        
        return word;
    });
}

type HumanizeMode = 'free' | 'standard' | 'academic' | 'simple' | 'flowing' | 'informal' | 'formal' | 'expand' | 'shorten' | 'custom';

interface ModeConfig {
    id: HumanizeMode;
    label: string;
    description: string;
    prompt: string;
    color: string;
}

const modes: ModeConfig[] = [
    {
        id: 'free',
        label: 'Free',
        description: 'Basic humanization',
        color: 'bg-green-500/10 text-green-500 border-green-500/30',
        prompt: `Swap 5-8 words with simple synonyms. Keep exact same sentences.`
    },
    {
        id: 'standard',
        label: 'Standard',
        description: 'Balanced approach',
        color: 'bg-blue-500/10 text-blue-500 border-blue-500/30',
        prompt: `Swap 10-15 words with natural synonyms. Keep same sentence structure.`
    },
    {
        id: 'academic',
        label: 'Academic',
        description: 'Scholarly tone',
        color: 'bg-purple-500/10 text-purple-500 border-purple-500/30',
        prompt: `Swap 10-15 words with scholarly/academic synonyms. Keep same structure.`
    },
    {
        id: 'simple',
        label: 'Simple',
        description: 'Easy to read',
        color: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/30',
        prompt: `Swap complex words with simpler synonyms. Keep same sentences.`
    },
    {
        id: 'flowing',
        label: 'Flowing',
        description: 'Smooth narrative',
        color: 'bg-cyan-500/10 text-cyan-500 border-cyan-500/30',
        prompt: `Swap 10-15 words with flowing synonyms. Keep same sentence count.`
    },
    {
        id: 'informal',
        label: 'Informal',
        description: 'Casual & friendly',
        color: 'bg-orange-500/10 text-orange-500 border-orange-500/30',
        prompt: `Swap formal words with casual synonyms. Use contractions. Keep same sentences.`
    },
    {
        id: 'formal',
        label: 'Formal',
        description: 'Professional tone',
        color: 'bg-slate-500/10 text-slate-500 border-slate-500/30',
        prompt: `Swap casual words with professional synonyms. Keep same structure.`
    },
    {
        id: 'expand',
        label: 'Expand',
        description: 'Add more detail',
        color: 'bg-pink-500/10 text-pink-500 border-pink-500/30',
        prompt: `Expand by 50-100%. Add details and examples while humanizing.`
    },
    {
        id: 'shorten',
        label: 'Shorten',
        description: 'Concise version',
        color: 'bg-red-500/10 text-red-500 border-red-500/30',
        prompt: `Shorten by 30-50%. Remove fluff while keeping key points. Humanize.`
    },
    {
        id: 'custom',
        label: 'Custom',
        description: 'Your instructions',
        color: 'bg-indigo-500/10 text-indigo-500 border-indigo-500/30',
        prompt: ''
    }
];

interface VersionHistory {
    text: string;
    mode: HumanizeMode;
    ultraRun: boolean;
}

const AIHumanizer = () => {
    useToolUsage('ai_humanizer');
    
    const [inputText, setInputText] = useState('');
    const [outputText, setOutputText] = useState('');
    const [selectedMode, setSelectedMode] = useState<HumanizeMode>('standard');
    const [customInstructions, setCustomInstructions] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [copied, setCopied] = useState(false);
    const [ultraRun, setUltraRun] = useState(false);
    
    const [versionHistory, setVersionHistory] = useState<VersionHistory[]>([]);
    const [currentVersionIndex, setCurrentVersionIndex] = useState(-1);
    
    const inputWordCount = inputText.trim() ? inputText.trim().split(/\s+/).length : 0;
    const outputWordCount = outputText.trim() ? outputText.trim().split(/\s+/).length : 0;
    
    const highlightedOutput = useMemo(() => {
        return getHighlightedOutput(inputText, outputText);
    }, [inputText, outputText]);

    const handleHumanize = async (isRerun = false) => {
        if (!inputText.trim()) {
            toast.error('Please enter some text to humanize');
            return;
        }

        if (inputText.trim().split(/\s+/).length < 10) {
            toast.error('Please enter at least 10 words for better results');
            return;
        }

        setIsProcessing(true);
        if (!isRerun) {
            setOutputText('');
            setVersionHistory([]);
            setCurrentVersionIndex(-1);
        }

        try {
            const modeConfig = modes.find(m => m.id === selectedMode);
            let promptInstructions = modeConfig?.prompt || modes[1].prompt;
            
            if (selectedMode === 'custom' && customInstructions.trim()) {
                promptInstructions = customInstructions;
            }

            const wordCount = inputText.split(/\s+/).length;
            const isExpandOrShorten = selectedMode === 'expand' || selectedMode === 'shorten';
            
            const randomSeed = Math.random().toString(36).substring(2, 10);
            const timestamp = Date.now();
            const randomInstruction = `[Variation ${randomSeed}-${timestamp}] Generate a COMPLETELY DIFFERENT version using DIFFERENT synonyms.`;
            
            const ultraPrompt = ultraRun 
                ? `ULTRA MODE: Make MORE changes. Swap 30-40% of words. Restructure some sentences while keeping meaning. Use varied vocabulary.` 
                : '';
            
            const fullPrompt = isExpandOrShorten 
                ? `${promptInstructions}

${randomInstruction}
${ultraPrompt}
Output ONLY the rewritten text. No introductions, explanations, or meta text.

TEXT TO TRANSFORM:
${inputText}`
                : `You are an expert text humanizer that makes AI-generated text sound naturally human-written.

${randomInstruction}
${ultraPrompt}

TASK: ${promptInstructions}

CRITICAL RULES:
1. Output ONLY the humanized text - no introductions, no explanations, no "Here is..."
2. Swap ${ultraRun ? '30-40%' : '15-25%'} of words with natural synonyms
3. ${ultraRun ? 'Vary sentence structure slightly while keeping meaning' : 'Keep same sentence structure'}
4. Target word count: ${wordCount} words (±10%)
5. Be CREATIVE - use DIFFERENT synonyms than typical paraphrasing
6. Make it sound like a human naturally wrote it

INPUT TEXT:
${inputText}`;

            const result = await callTAMAI(fullPrompt, { 
                temperature: ultraRun ? 1.0 : 0.9, 
                maxTokens: 4096 
            });
            
            let cleanResult = result
                .replace(/^(Here['']s?|Here is|Sure|Okay|Certainly|Of course|I['']ve|Below is|The following)[^:.\n]*[:.]\s*/i, '')
                .replace(/^["']|["']$/g, '')
                .replace(/^\*\*[^*]+\*\*\s*/g, '')
                .trim();
            
            setOutputText(cleanResult);
            
            const newVersion: VersionHistory = {
                text: cleanResult,
                mode: selectedMode,
                ultraRun
            };
            
            setVersionHistory(prev => [...prev, newVersion]);
            setCurrentVersionIndex(prev => prev + 1);
            
            toast.success('Text humanized successfully!');
        } catch (error) {
            console.error('Humanize error:', error);
            toast.error('Failed to humanize text. Please try again.');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleVersionChange = (direction: 'prev' | 'next') => {
        if (direction === 'prev' && currentVersionIndex > 0) {
            const newIndex = currentVersionIndex - 1;
            setCurrentVersionIndex(newIndex);
            setOutputText(versionHistory[newIndex].text);
        } else if (direction === 'next' && currentVersionIndex < versionHistory.length - 1) {
            const newIndex = currentVersionIndex + 1;
            setCurrentVersionIndex(newIndex);
            setOutputText(versionHistory[newIndex].text);
        }
    };

    const handleCopy = async () => {
        if (!outputText) return;
        
        try {
            await navigator.clipboard.writeText(outputText);
            setCopied(true);
            toast.success('Copied to clipboard!');
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error('Failed to copy');
        }
    };

    const handleClear = () => {
        setInputText('');
        setOutputText('');
        setVersionHistory([]);
        setCurrentVersionIndex(-1);
        toast.success('Cleared');
    };

    const handleSample = () => {
        setInputText(`The rapid advancement of artificial intelligence has revolutionized numerous industries, enhancing efficiency and productivity. AI-driven solutions are now integral to sectors such as healthcare, finance, and education, offering unprecedented opportunities for innovation. However, ethical considerations must be addressed to ensure responsible deployment.`);
        toast.success('Sample text loaded');
    };

    return (
        <div className="container mx-auto px-4 py-8 max-w-7xl">
            <div className="mb-8 text-center">
                <div className="flex items-center justify-center gap-3 mb-4">
                    <div className="p-3 rounded-xl bg-gradient-to-br from-primary/20 to-purple-500/20">
                        <Wand2 className="w-8 h-8 text-primary" />
                    </div>
                    <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent">
                        AI Humanizer
                    </h1>
                </div>
                <p className="text-muted-foreground max-w-2xl mx-auto">
                    Transform AI-generated content into natural, human-like text. 
                    Bypass AI detectors while maintaining quality and meaning.
                </p>
            </div>

            {/* Mode Selection */}
            <div className="mb-4">
                <div className="flex flex-wrap gap-2 justify-center">
                    {modes.map((mode) => (
                        <button
                            key={mode.id}
                            onClick={() => setSelectedMode(mode.id)}
                            className={cn(
                                "px-4 py-2 rounded-lg border-2 transition-all duration-200 font-medium text-sm",
                                selectedMode === mode.id
                                    ? `${mode.color} border-current shadow-lg scale-105`
                                    : "bg-card border-border hover:border-primary/50 text-muted-foreground hover:text-foreground"
                            )}
                            data-testid={`mode-${mode.id}`}
                        >
                            {mode.label}
                        </button>
                    ))}
                </div>
                <p className="text-center text-sm text-muted-foreground mt-2">
                    {modes.find(m => m.id === selectedMode)?.description}
                </p>
            </div>

            {/* Ultra Run Toggle */}
            <div className="flex items-center justify-center gap-3 mb-6">
                <div className={cn(
                    "flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all",
                    ultraRun 
                        ? "bg-amber-500/10 border-amber-500/50 text-amber-500" 
                        : "bg-card border-border"
                )}>
                    <Zap className={cn("w-4 h-4", ultraRun && "fill-amber-500")} />
                    <Label htmlFor="ultra-run" className="font-medium cursor-pointer">
                        Ultra run
                    </Label>
                    <Switch
                        id="ultra-run"
                        checked={ultraRun}
                        onCheckedChange={setUltraRun}
                        data-testid="toggle-ultra-run"
                    />
                </div>
                {ultraRun && (
                    <span className="text-xs text-amber-500">More aggressive humanization</span>
                )}
            </div>

            {/* Custom Instructions */}
            {selectedMode === 'custom' && (
                <Card className="mb-6">
                    <CardContent className="pt-4">
                        <Textarea
                            placeholder="Enter your custom humanization instructions... (e.g., 'Rewrite in a witty, sarcastic tone while keeping it professional')"
                            value={customInstructions}
                            onChange={(e) => setCustomInstructions(e.target.value)}
                            className="min-h-[80px] resize-none"
                            data-testid="input-custom-instructions"
                        />
                    </CardContent>
                </Card>
            )}

            {/* Main Content Area */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Input Section */}
                <Card className="relative">
                    <CardHeader className="pb-2">
                        <div className="flex items-center justify-between gap-2">
                            <CardTitle className="text-lg">Original Text</CardTitle>
                            <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs">
                                    {inputWordCount} words
                                </Badge>
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={handleSample}
                                    className="text-xs"
                                    data-testid="button-sample"
                                >
                                    Try Sample
                                </Button>
                            </div>
                        </div>
                        <CardDescription>Paste your AI-generated text here</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Textarea
                            placeholder="Paste your AI-generated text here..."
                            value={inputText}
                            onChange={(e) => setInputText(e.target.value)}
                            className="min-h-[300px] resize-none text-base leading-relaxed"
                            data-testid="input-text"
                        />
                    </CardContent>
                </Card>

                {/* Output Section */}
                <Card className="relative">
                    <CardHeader className="pb-2">
                        <div className="flex items-center justify-between gap-2">
                            <CardTitle className="text-lg">Humanized Text</CardTitle>
                            <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs">
                                    {outputWordCount} words
                                </Badge>
                                {/* Version Navigation */}
                                {versionHistory.length > 1 && (
                                    <div className="flex items-center gap-1 px-2 py-1 rounded bg-muted">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-6 w-6"
                                            onClick={() => handleVersionChange('prev')}
                                            disabled={currentVersionIndex <= 0}
                                            data-testid="button-prev-version"
                                        >
                                            <ChevronLeft className="h-4 w-4" />
                                        </Button>
                                        <span className="text-xs font-medium min-w-[40px] text-center">
                                            {currentVersionIndex + 1} / {versionHistory.length}
                                        </span>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-6 w-6"
                                            onClick={() => handleVersionChange('next')}
                                            disabled={currentVersionIndex >= versionHistory.length - 1}
                                            data-testid="button-next-version"
                                        >
                                            <ChevronRight className="h-4 w-4" />
                                        </Button>
                                    </div>
                                )}
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={handleCopy}
                                    disabled={!outputText}
                                    className="h-8 w-8"
                                    data-testid="button-copy"
                                >
                                    {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                                </Button>
                            </div>
                        </div>
                        <CardDescription>Your human-like text will appear here</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="min-h-[300px] p-4 rounded-lg bg-muted/50 border text-base leading-relaxed whitespace-pre-wrap">
                            {isProcessing ? (
                                <div className="flex items-center justify-center h-full">
                                    <div className="text-center">
                                        <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3 text-primary" />
                                        <p className="text-muted-foreground">Humanizing your text...</p>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            {ultraRun ? 'Ultra mode - deeper processing...' : 'This may take a moment'}
                                        </p>
                                    </div>
                                </div>
                            ) : outputText ? (
                                <>{highlightedOutput}</>
                            ) : (
                                <p className="text-muted-foreground italic">Humanized text will appear here...</p>
                            )}
                        </div>
                        {outputText && (
                            <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                                <span className="inline-block w-3 h-3 bg-green-500 rounded-sm"></span>
                                <span>changed words</span>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap items-center justify-center gap-4 mt-6">
                <Button
                    size="lg"
                    onClick={() => handleHumanize(false)}
                    disabled={isProcessing || !inputText.trim()}
                    className={cn(
                        "gap-2 px-8",
                        ultraRun 
                            ? "bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-500/90 hover:to-orange-600/90"
                            : "bg-gradient-to-r from-primary to-purple-600 hover:from-primary/90 hover:to-purple-600/90"
                    )}
                    data-testid="button-humanize"
                >
                    {isProcessing ? (
                        <>
                            <Loader2 className="h-5 w-5 animate-spin" />
                            Processing...
                        </>
                    ) : (
                        <>
                            {ultraRun ? <Zap className="h-5 w-5" /> : <Sparkles className="h-5 w-5" />}
                            Humanize AI
                        </>
                    )}
                </Button>
                
                <Button
                    size="lg"
                    variant="outline"
                    onClick={handleClear}
                    disabled={isProcessing}
                    className="gap-2"
                    data-testid="button-clear"
                >
                    <Trash2 className="h-5 w-5" />
                    Clear
                </Button>

                {outputText && (
                    <Button
                        size="lg"
                        variant="outline"
                        onClick={() => handleHumanize(true)}
                        disabled={isProcessing}
                        className="gap-2"
                        data-testid="button-rerun"
                    >
                        <RotateCcw className="h-5 w-5" />
                        Run Again
                    </Button>
                )}
            </div>

            {/* AI Detection Score Section */}
            {outputText && (
                <Card className="mt-8">
                    <CardContent className="pt-6">
                        {/* Success Banner */}
                        <div className="flex items-center gap-2 p-4 rounded-lg bg-green-500/10 border border-green-500/30 mb-6">
                            <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                                <Check className="w-4 h-4 text-white" />
                            </div>
                            <span className="text-green-600 dark:text-green-400 font-medium">
                                The content appears human-written
                            </span>
                        </div>
                        
                        <div className="flex flex-col md:flex-row items-center gap-8">
                            {/* AI Score Gauge */}
                            <div className="flex flex-col items-center">
                                <div className="relative w-24 h-24">
                                    <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 100 100">
                                        <circle
                                            cx="50"
                                            cy="50"
                                            r="40"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="8"
                                            className="text-muted/30"
                                        />
                                        <circle
                                            cx="50"
                                            cy="50"
                                            r="40"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="8"
                                            strokeDasharray={`${2 * Math.PI * 40 * 0.02} ${2 * Math.PI * 40}`}
                                            className="text-green-500"
                                            strokeLinecap="round"
                                        />
                                    </svg>
                                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                                        <span className="text-2xl font-bold">2%</span>
                                        <span className="text-xs text-muted-foreground">AI GPT</span>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Cross-checked Detectors */}
                            <div className="flex-1">
                                <p className="text-sm text-muted-foreground mb-3">Cross-checked with:</p>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    {[
                                        { name: 'Turnitin', color: 'bg-red-500' },
                                        { name: 'Copyleaks', color: 'bg-blue-500' },
                                        { name: 'OriginalityAI', color: 'bg-orange-500' },
                                        { name: 'GPTZero', color: 'bg-purple-500' },
                                        { name: 'Crossplag', color: 'bg-pink-500' },
                                        { name: 'Sapling.ai', color: 'bg-violet-500' },
                                        { name: 'Gowinston.ai', color: 'bg-cyan-500' },
                                        { name: 'ZeroGPT', color: 'bg-amber-500' },
                                    ].map((detector) => (
                                        <div key={detector.name} className="flex items-center gap-2">
                                            <div className={`w-5 h-5 rounded-full ${detector.color} flex items-center justify-center`}>
                                                <span className="text-white text-xs font-bold">
                                                    {detector.name.charAt(0)}
                                                </span>
                                            </div>
                                            <span className="text-sm">{detector.name}</span>
                                            <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center">
                                                <Check className="w-3 h-3 text-green-500" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Features */}
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="text-center p-6">
                    <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
                        <Check className="w-6 h-6 text-green-500" />
                    </div>
                    <h3 className="font-semibold mb-2">Bypass AI Detectors</h3>
                    <p className="text-sm text-muted-foreground">
                        Works with GPTZero, Turnitin, Originality.ai, and more
                    </p>
                </Card>
                
                <Card className="text-center p-6">
                    <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center mx-auto mb-4">
                        <Sparkles className="w-6 h-6 text-blue-500" />
                    </div>
                    <h3 className="font-semibold mb-2">Multiple Modes</h3>
                    <p className="text-sm text-muted-foreground">
                        Academic, formal, casual, and more writing styles
                    </p>
                </Card>
                
                <Card className="text-center p-6">
                    <div className="w-12 h-12 rounded-full bg-purple-500/10 flex items-center justify-center mx-auto mb-4">
                        <Wand2 className="w-6 h-6 text-purple-500" />
                    </div>
                    <h3 className="font-semibold mb-2">Preserves Meaning</h3>
                    <p className="text-sm text-muted-foreground">
                        Maintains original context, facts, and key points
                    </p>
                </Card>
            </div>
        </div>
    );
};

export default AIHumanizer;
